import React, {FC, useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, FilterBar, ModalVariables, SearchComponent, StartPage } from "../../../components";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { billingService } from "../../../api";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { icons } from "../../../icons";
import { useUi } from "../../../context/ui.context";
import { handleStatusName } from "../../../utils/handle-status-name";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { StatusText } from "../service-page/styles";
import { usePageTitle } from "../../../context/navigationContext";
const SERVICE_FILTER_KEYS = [
  'created_at',
  'updated_at',
  'activated_at',
  'suspended_at',
  'terminated_at',
  'status',
  'paid_until',
  'client',
  'product',
];
const SERVICE_FILTER_TYPES: Record<string, 'date' | 'number' | 'text' | undefined> = {
  created_at: 'date',
  updated_at: 'date',
  activated_at: 'date',
  suspended_at: 'date',
  terminated_at: 'date',
  status: undefined,
  paid_until: 'date',
  client: undefined,
  product: undefined,
};
const DEFAULT_ORDERING = '-created_at';

const Services:FC = () => {
  const navigate = useNavigate()
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const [services, setServices] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [servicesPageData, setServicesPageData] = useState<any[]>([])
  const [t] = useTranslation()
  usePageTitle(t("PAGE_TITLE_SERVICES"));
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>()
  const [serviceId, setServiceId] = useState<any>()
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const {viewAlert} = useUi()
  const [filterOptions, setFilterOptions] = useState<any>()
  const normalizedFilterOptions = useMemo(() => {
    return SERVICE_FILTER_KEYS.reduce((acc:any, key) => {
      acc[key] = {
        options: filterOptions?.[key] || [],
        type: SERVICE_FILTER_TYPES[key],
      };
      return acc;
    }, {});
  }, [filterOptions]);
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
  }
  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }
  const getServices = () => {
    setLoading(true)
    billingService.getServices({
      page:page || 1,
      page_size:pageSize || 10,
      ordering:ordering || DEFAULT_ORDERING,
      search:searchString||'',
      filtering: filtering || '',
    })
    .then((res:any) => {
      setServices(res.data.objects.map((service:any) => {
        return {
          ...service,
          name:service?.display_name || service?.product?.name || 'Service',
          client: service?.client ? (
            <a href={`/clients-users/clients/${service.client.id}`}>
              {service.client.display || service.client.name || service.client.id}
            </a>
          ) : '-',
          product: service?.product?.name || '-',
          status:<StatusText status={service.status}>{handleStatusName(service.status, t)}</StatusText>,
          created_at:timeConverterMMDDYYHHMM(service.created_at),
          navigate:service.id.toString(),
          paid_until:timeConverterMMDDYYHHMM(service.paid_until),
        }
      }))
      setServicesPageData(res.data)
      setFilterOptions(res.data?.filter_options);
      setPageLoading(false)
      setLoading(false)
    })
    .catch((e:any) => {
      console.log('error', e);
    })
  }
  useEffect(() => {
    getServices()
    const interval = setInterval(() => {
      getServices()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  },[page,
    ordering,
    pageSize,
    searchString,
    filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);
  const items = [
    {
      title:t("EDIT"),
      id:'edit',
      icon:icons.Edit || '✏️',
      divider:false,
      onClick:(service:any) => {
        navigate(`/billing/services/${service.id}/edit`)
      },
    },
    {
      title:t("UPGRADE_DOWNGRADE"),
      id:'upgrade',
      icon:icons.Upgrade || '⬆️',
      divider:false,
      onClick:(service:any) => {
        if (['pending', 'active'].includes(service.status)) {
          navigate(`/billing/services/${service.id}/upgrade`)
        }
      },
    },
    {
      title:t("UNSHELVE"),
      id:'23',
      icon:icons.Shelve,
      divider:false,
      onClick:(service:any) => {
        setServiceId(service.id)
        setAlertTitle(t("UNSHELVE_ALL_INSTANCES_TITLE"))
        setAlertSubTitle(t("UNSHELVE_ALL_INSTANCES_SUBTITLE"))
        openModal('universal')
      },
    },
    {
      title:t("SHELVE"),
      id:'24',
      icon:icons.Unshelve,
      divider:false,
      onClick:(service:any) => {
        setServiceId(service.id)
        setAlertTitle(t("SHELVE_ALL_INSTANCES_TITLE"))
        setAlertSubTitle(t("SHELVE_ALL_INSTANCES_SUBTITLE"))
        openModal('universal')
      },
    },
  ]
  const closeModal = () => {
    if (modalType !== 'universal' || !serviceId) return;
    setLoading(true);
    const data = {
      action: alertTitle === t("SHELVE_ALL_INSTANCES_TITLE") ? 'shelve' : 'unshelve'
    };
  
    billingService.executeActionAllInstances(serviceId, data)
      .then(handleSuccess)
      .catch(handleError)
      .finally(() => {
        setLoading(false);
        setViewModal(false);
        setActiveAnimation(false);
      });
  
    function handleSuccess(res:any) {
      viewAlert({
        severity: 'info',
        message: res.data.detail
      });
    }
  
    function handleError(err:any) {
      if (err?.response?.data?.detail) {
        viewAlert({
          severity: 'error',
          message: err.response?.data?.detail
        });
      }
    }
  };

const ColumnsServices = [
  {
    title:t("NAME"),
    key:'name',
    sorted:true,
  },
  {
    title:t("PRODUCT"),
    key:'product',
    sorted:true,
  },
  {
    title:t("CLIENT"),
    key:'client',
    sorted:true,
  },
  {
    title:t("CREATED_AT"),
    key:'created_at',
    sorted:true,
  },
  {
    title:t("PAID_UNTIL"),
    key:'paid_until',
    sorted:true,
  },
  {
    title:t("STATUS"),
    key:'status',
    sorted:true,
  },
  {
    title:'',
    key:'props',
  }
]

  return (
    <>
      <StartPage
        title={t("SERVICES")}
        noResourceText={t("NO_SERVICES", { defaultValue: "No services" })}
        haveResource={services.length > 0}
        pageLoading={pageLoading}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <Button
                title={t("CLEAR_FILTERS")}
                onClick={handleResetFilters}
                variant='stroke'
                size='display'
                disabled={
                  (!filtering || filtering === '') &&
                  !searchString &&
                  ordering === DEFAULT_ORDERING
                }
              />
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        tableData={{
          columns:ColumnsServices,
          data:services,
          hideCheckbox:true,
          props:items,
          paginationData:servicesPageData,
          updateData:getServices
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        alertSubTitle={alertSubTitle}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        loading={loading}
        confirmButtonText={t("CONFIRM")}
        cancelButtonText={t("CANCEL")}
      />
    </>
  )
}

export default Services
